import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card } from '../components/ui/card';
import { Check, Shield, Clock, Award, Users } from 'lucide-react';
import { pricingPlans } from '../mock';
import { apiService } from '../services/api';
import { toast } from 'sonner';

const serviceContent = {
  trademark: {
    title: 'Trademark Registration Services',
    subtitle: 'Protect Your Brand Identity with Federal Trademark Registration',
    description: 'Secure exclusive rights to your brand name, logo, and slogan with our comprehensive trademark registration services. Our experienced attorneys guide you through every step of the USPTO filing process.',
    benefits: [
      'Nationwide exclusive rights to your brand',
      'Legal presumption of ownership',
      'Ability to use ® symbol',
      'Enhanced protection against infringement',
      'Increased brand value and credibility',
      'Amazon Brand Registry eligibility'
    ],
    process: [
      'Comprehensive trademark search',
      'Application preparation and filing',
      'USPTO examination support',
      'Office action responses',
      'Registration certificate delivery'
    ]
  },
  copyright: {
    title: 'Copyright Registration Services',
    subtitle: 'Protect Your Creative Works with Federal Copyright Registration',
    description: 'Safeguard your original creative works including books, music, art, software, and more. Our copyright registration services provide comprehensive legal protection for your intellectual creations.',
    benefits: [
      'Public record of your copyright claim',
      'Legal evidence of ownership',
      'Ability to sue for infringement',
      'Statutory damages eligibility',
      'Attorney fees recovery rights',
      'International protection through treaties'
    ],
    process: [
      'Work identification and classification',
      'Application preparation',
      'Copyright Office filing',
      'Examination monitoring',
      'Certificate of registration'
    ]
  },
  patent: {
    title: 'Patent Services',
    subtitle: 'Protect Your Inventions with Comprehensive Patent Services',
    description: 'Secure patent protection for your innovations with our full-service patent practice. From utility patents to design patents, we help inventors protect their groundbreaking ideas.',
    benefits: [
      'Exclusive rights to your invention',
      'Prevent others from making, using, or selling',
      'Increased business valuation',
      'Licensing and monetization opportunities',
      'Competitive market advantage',
      '20-year protection period'
    ],
    process: [
      'Prior art search and analysis',
      'Patentability assessment',
      'Patent application drafting',
      'USPTO prosecution',
      'Patent grant and maintenance'
    ]
  },
  'company-formation': {
    title: 'Company Formation Services',
    subtitle: 'Establish Your Business with Proper Legal Structure',
    description: 'Start your business on solid legal footing with our company formation services. We help you choose and establish the right business structure for your needs.',
    benefits: [
      'Limited liability protection',
      'Tax optimization opportunities',
      'Enhanced business credibility',
      'Easier access to funding',
      'Perpetual existence',
      'Professional business structure'
    ],
    process: [
      'Business structure consultation',
      'Name availability check',
      'Articles of incorporation/organization',
      'EIN acquisition',
      'Operating agreement/bylaws'
    ]
  }
};

const ServicePage = () => {
  const { serviceType } = useParams();
  const content = serviceContent[serviceType] || serviceContent.trademark;

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const response = await apiService.createServiceInquiry({
        serviceType: serviceType || 'trademark',
        ...formData
      });
      toast.success(response.message);
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit inquiry. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-16">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#2B0A12] via-[#3D1C24] to-[#2B0A12] py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-bold text-[#F1F1F1] mb-6">{content.title}</h1>
          <p className="text-2xl text-[#FFB400] mb-4">{content.subtitle}</p>
          <p className="text-xl text-gray-300 max-w-4xl mx-auto">{content.description}</p>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <h2 className="text-4xl font-bold text-[#2B0A12] mb-6">Key Benefits</h2>
              <ul className="space-y-4">
                {content.benefits.map((benefit, index) => (
                  <li key={index} className="flex items-start space-x-3">
                    <Check className="h-6 w-6 text-[#FFB400] flex-shrink-0 mt-1" />
                    <span className="text-lg text-gray-700">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h2 className="text-4xl font-bold text-[#2B0A12] mb-6">Our Process</h2>
              <div className="space-y-4">
                {content.process.map((step, index) => (
                  <div key={index} className="flex items-start space-x-4">
                    <div className="w-10 h-10 bg-[#FFB400] rounded-full flex items-center justify-center flex-shrink-0 text-[#2B0A12] font-bold">
                      {index + 1}
                    </div>
                    <div>
                      <p className="text-lg text-gray-700 mt-2">{step}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-[#2B0A12] text-center mb-12">Why Choose Us</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { icon: Shield, title: 'Expert Attorneys', desc: 'Licensed IP attorneys with decades of experience' },
              { icon: Clock, title: 'Fast Processing', desc: 'Quick turnaround with expedited options available' },
              { icon: Award, title: 'Proven Results', desc: 'High success rate in registrations and approvals' },
              { icon: Users, title: 'Dedicated Support', desc: 'Personal attention throughout the entire process' }
            ].map((item, index) => (
              <Card key={index} className="p-6 text-center hover:shadow-xl transition-shadow">
                <item.icon className="h-12 w-12 text-[#FFB400] mx-auto mb-4" />
                <h3 className="text-xl font-bold text-[#2B0A12] mb-2">{item.title}</h3>
                <p className="text-gray-600">{item.desc}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-[#2B0A12] text-center mb-12">Transparent Pricing</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {pricingPlans.map((plan) => (
              <Card
                key={plan.id}
                className={`p-6 ${
                  plan.popular ? 'border-4 border-[#D72638] shadow-2xl' : 'border-2 border-gray-200'
                }`}
              >
                {plan.popular && (
                  <div className="bg-gradient-to-r from-[#D72638] to-[#FFB400] text-white text-xs font-bold px-3 py-1 rounded-full inline-block mb-4">
                    MOST POPULAR
                  </div>
                )}
                <h3 className="text-2xl font-bold text-[#2B0A12] mb-2">{plan.name}</h3>
                <div className="mb-4">
                  <span className="text-4xl font-bold text-[#D72638]">${plan.price}</span>
                  <p className="text-sm text-gray-500">+ Federal Filing Fee</p>
                </div>
                <p className="text-sm text-gray-600 mb-4">{plan.description}</p>
                <Button className="w-full bg-[#FFB400] hover:bg-[#e5a200] text-[#2B0A12] font-semibold">
                  Get Started
                </Button>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-20 bg-gradient-to-br from-[#2B0A12] via-[#3D1C24] to-[#2B0A12]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-4xl font-bold text-[#F1F1F1] mb-4">Get Started Today</h2>
            <p className="text-lg text-gray-300">Contact us for a free consultation</p>
          </div>
          <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-2xl p-8 space-y-4">
            <Input
              type="text"
              placeholder="Full Name *"
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              required
            />
            <Input
              type="email"
              placeholder="Email Address *"
              value={formData.email}
              onChange={(e) => setFormData({...formData, email: e.target.value})}
              required
            />
            <Input
              type="tel"
              placeholder="Phone Number *"
              value={formData.phone}
              onChange={(e) => setFormData({...formData, phone: e.target.value})}
              required
            />
            <Textarea
              placeholder="Tell us about your needs *"
              value={formData.message}
              onChange={(e) => setFormData({...formData, message: e.target.value})}
              required
              rows={4}
            />
            <Button
              type="submit"
              disabled={isLoading}
              className="w-full bg-[#FFB400] hover:bg-[#e5a200] text-[#2B0A12] font-bold text-lg py-6"
            >
              {isLoading ? 'Submitting...' : 'Request Free Consultation'}
            </Button>
          </form>
        </div>
      </section>
    </div>
  );
};

export default ServicePage;